// Define directive types
export enum DirectiveType {
  NOOP = 0,
  WAKE = 1,
  SLEEP = 2,
  COMMAND = 3,
  STOP = 4,  // Immediate stop - aborts LLM/TTS but keeps agent enabled
}

// Directive command strings that trigger directive detection on the server
export enum DirectiveCommands {
  WAKE = 'telly',
  SLEEP = 'telly sleep',  // Disable agent completely (requires re-wake)
  STOP = 'telly stop',    // Immediately stop current response (agent stays enabled)
}

// Message type classification for transcript routing
export enum MessageType {
  CHAT = 'chat',          // Regular conversation - stored in chat history
  COMMAND = 'command',    // Control signal (e.g., PTT) - processed but NOT stored
  DIRECTIVE = 'directive', // Detected directive message
  ACTIVITY = 'activity',  // Activity-generated message
}

interface BaseChatMessage {
  messageId: string;
  roomId: string;
  userId: string;
  userAlias: string;
  message: string;
  timestamp: number;
  isFinal: boolean;
  [key: string]: any;
}

export interface ChatHistoryMessage extends BaseChatMessage {
  messageType: `${MessageType}`;
  /** Distinguishes prior companion claims from participant-supplied evidence. */
  speakerProvenance?: 'participant' | 'companion';
  directiveType?: DirectiveType;
  directiveMessage?: string;
  /**
   * #4278 — for a WAKE message, the words that came BEFORE the wake phrase ("" when it led the
   * sentence). "Nova, stop the music" and "why did Nova stop the music" have the same
   * `directiveMessage`; only this tells a request from a sentence about the companion.
   */
  directivePrefix?: string;
  /**
   * Category tag for non-LLM companion utterances (voice acks, search acks,
   * presentation card narration). Absent for normal chat + LLM-response rows.
   * Transcript exports use this to label ack-like rows distinctly.
   */
  speechKind?: string;
}

/**
 * Predicate for history rows that should be fed back into LLM prompts.
 * Companion acks / narration are persisted for transcript coverage but are
 * meta-utterances about the conversation, not part of it — feeding them into
 * prompts risks the LLM mimicking its own ack cadence. Transcript export
 * uses a different path and intentionally keeps these rows.
 */
export const isLlmEligibleChatMessage = (msg: ChatHistoryMessage): boolean => !msg.speechKind;

// Define PriorityMessage interface (formerly QueueMessage)
export interface PriorityMessage extends BaseChatMessage {
  messageType: MessageType.DIRECTIVE;
  directiveType: DirectiveType;
  directiveMessage?: string;
}

// --- Transcript interfaces with sourceType discriminator ---

export interface TranscriptBase extends BaseChatMessage {
  /** Indicates origin of the transcript */
  sourceType: 'user' | 'activity';
  messageType: MessageType.CHAT | MessageType.COMMAND;
}

export interface UserTranscript extends TranscriptBase {
  sourceType: 'user';
}

export interface ActivityTranscript extends TranscriptBase {
  sourceType: 'activity';
}
